import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Textarea,
  Input,
} from '@roger/ui-components';
import { searchDiagnosisCodes } from '../services/rogerDocApi';
import { getManualDiagnosisLogMessage } from '../utils/dateUtils';
import { Edit, Loader2, Search } from 'lucide-react';
import type {
  DiagnosisWithReasoning,
  PrimaryDiagnosis,
  DiagnosisCode,
} from '../types/codingData.types';

interface AddDiagnosisModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (diagnosis: DiagnosisWithReasoning) => void;
  isEditing: boolean;
  isPrimaryDiagnosis: boolean;
  currentDiagnosis: DiagnosisWithReasoning | PrimaryDiagnosis | null;
  diagnosisPosition: string | number | null;
}

const AddDiagnosisModal: React.FC<AddDiagnosisModalProps> = ({
  open,
  onClose,
  onConfirm,
  isEditing,
  isPrimaryDiagnosis,
  currentDiagnosis,
  diagnosisPosition,
}) => {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<DiagnosisCode[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedCode, setSelectedCode] = useState<DiagnosisCode | null>(null);
  const [reasoning, setReasoning] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Load current diagnosis data when editing
  useEffect(() => {
    if (open && isEditing && currentDiagnosis) {
      // setSelectedCode(currentDiagnosis.diagnosis);
      // setReasoning(currentDiagnosis.reasoning || '');
      setSearchText('');
      setSearchResults([]);
      setHasSearched(false);
    } else if (open && !isEditing) {
      // Reset for adding new
      setSelectedCode(null);
      setReasoning('');
      setSearchText('');
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [open, isEditing, currentDiagnosis]);

  const handleSearch = async () => {
    if (!searchText.trim()) return;

    setSearching(true);
    setHasSearched(true);
    try {
      const results = (await searchDiagnosisCodes(
        searchText
      )) as DiagnosisCode[];
      setSearchResults(results || []);
    } catch (error) {
      console.error('Error searching diagnosis codes:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleConfirm = () => {
    if (!selectedCode) return;

    // Generate timestamp suffix
    const suffix = `\n\n${getManualDiagnosisLogMessage()}\n\n`;

    onConfirm({
      diagnosis: selectedCode,
      reasoning: suffix + reasoning.trim(),
    });

    // Reset form
    setSelectedCode(null);
    setReasoning('');
    setSearchText('');
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleClose = () => {
    setSelectedCode(null);
    setReasoning('');
    setSearchText('');
    setSearchResults([]);
    setHasSearched(false);
    onClose();
  };

  const getTitle = () => {
    if (isEditing && currentDiagnosis) {
      const diagnosis = (currentDiagnosis as DiagnosisWithReasoning).diagnosis;
      const codeAndName = diagnosis
        ? ` ${diagnosis.name} ${diagnosis.desc}`
        : '';
      return isPrimaryDiagnosis
        ? `Replace Primary Diagnosis${codeAndName}`
        : `Replace Secondary Diagnosis #${diagnosisPosition}${codeAndName}`;
    }
    return 'Add Secondary Diagnosis';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-hidden flex flex-col'>
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription className='sr-only'>
            Search for and add a diagnosis code to the patient record
          </DialogDescription>
        </DialogHeader>

        <div className='flex-1 overflow-y-auto space-y-4 py-4'>
          {/* Search Section */}
          {!selectedCode && (
            <div className='space-y-3'>
              <label
                htmlFor='search'
                className='block text-sm font-medium text-gray-700'
              >
                Search Diagnosis Code
              </label>
              <div className='flex gap-2'>
                <Input
                  id='search'
                  type='text'
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder='Enter ICD-10 code or description...'
                  className='flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
                />
                <Button
                  onClick={handleSearch}
                  disabled={searching || !searchText.trim()}
                >
                  {searching ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    <Search className='w-4 h-4' />
                  )}
                  Search
                </Button>
              </div>

              {/* Search Results */}
              {hasSearched && (
                <div className='border border-gray-200 rounded-md max-h-96 overflow-y-auto'>
                  {searching ? (
                    <div className='p-4 text-center text-gray-500'>
                      <Loader2 className='w-6 h-6 animate-spin mx-auto mb-2' />
                      Searching...
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className='divide-y'>
                      {searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedCode(result);
                            setSearchResults([]);
                            setSearchText('');
                            setHasSearched(false);
                          }}
                          className='w-full p-3 text-left hover:bg-gray-50 transition-colors'
                        >
                          <div className='flex items-start gap-3'>
                            <div className='px-2 py-1 bg-primary text-white text-xs font-mono rounded'>
                              {result.name}
                            </div>
                            <div className='flex-1'>
                              <p className='text-sm font-medium text-gray-900'>
                                {result.desc}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className='p-4 text-center text-gray-500'>
                      No results found. Try a different search term.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Selected Code Display */}
          {selectedCode && (
            <div className='space-y-4'>
              <div>
                <div className='flex items-center justify-between'>
                  <div className='block text-sm font-medium text-gray-700'>
                    Selected Diagnosis
                  </div>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setSelectedCode(null)}
                  >
                    <Edit />
                    Change
                  </Button>
                </div>
                <div className='mt-2 p-4 bg-gray-50 border border-gray-200 rounded-md'>
                  <div className='flex items-start gap-3'>
                    <div className='px-3 py-1.5 bg-primary text-white font-mono rounded'>
                      {selectedCode.name}
                    </div>
                    <div className='flex-1'>
                      <p className='font-medium text-gray-900'>
                        {selectedCode.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor='reasoning'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Clinical Reasoning
                </label>
                <div className='mb-2 p-3 bg-gray-50 border border-gray-200 rounded-md'>
                  <p className='text-xs text-gray-700 italic'>
                    {getManualDiagnosisLogMessage()}
                  </p>
                </div>
                <Textarea
                  id='reasoning'
                  value={reasoning}
                  onChange={e => setReasoning(e.target.value)}
                  placeholder='Explain why this diagnosis is relevant...'
                  className='mt-2 min-h-[120px]'
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedCode}>
            {isEditing ? 'Save Changes' : 'Add Diagnosis'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddDiagnosisModal;
